import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';
import { GoalEmployee } from '../types';
import { GoalAvatar } from '../GoalAvatar';
import { useGoalSelection } from '../GoalBulkApproval';
import { useGoalsContext } from '../GoalsContext/GoalsContext';

const i18prefix = 'performanceReview.goals2.goalStatusCard.';

type Props = {
  employee: GoalEmployee;
  goalIds: readonly string[];
  errors: readonly { errorCode: string; errorMessage: string }[];
};

export const EmployeeGroupHeader: React.FC<Props> = ({
  employee,
  goalIds,
  errors,
}) => {
  const { t } = useTranslation();
  const { isManagerView } = useGoalsContext();
  const { selectedGoalIds, selectGoals } = useGoalSelection();

  const goalCount = goalIds.length;
  const hasErrors = errors.length > 0;

  const allSelected = useMemo(
    () => goalCount > 0 && goalIds.every((id) => selectedGoalIds.has(id)),
    [goalIds, goalCount, selectedGoalIds],
  );

  const handleSelectAll = useCallback(() => {
    selectGoals([...goalIds]);
  }, [selectGoals, goalIds]);

  return (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="d-flex align-items-center gap-2">
        <GoalAvatar
          initials={employee.initials}
          hintText={employee.name}
          avatarUrl={employee.avatarUrl}
        />
        <span className="fw-bold">{employee.name}</span>
        {hasErrors && (
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id={`employee-weight-validation-tooltip-${employee.id}`}>
                <ul className="list-unstyled text-start mb-0">
                  {errors.map((err, i) => (
                    <li key={i}>• {err.errorMessage}</li>
                  ))}
                </ul>
              </Tooltip>
            }>
            <span
              data-testid="employee-weight-validation-warning"
              className={classNames(
                'd-inline-flex',
                'align-items-center',
                'text-warning',
              )}>
              <FlairIcon icon="alert-triangle-outline" size="sm" />
            </span>
          </OverlayTrigger>
        )}
        {isManagerView && goalCount > 0 && !allSelected && (
          <span
            className="text-primary fs-6 ms-1"
            role="button"
            onClick={handleSelectAll}>
            {t(i18prefix + 'selectAll')}
          </span>
        )}
      </div>
      <div className="fs-4 fw-bold">
        {goalCount} {t(i18prefix + 'goal', { count: goalCount })}
      </div>
    </div>
  );
};
